import { FormEvent, ReactNode, useEffect, useState } from "react";
import { ArrowLeft, Banknote, Calendar, CircleSlash, Edit, KeyRound, Mail, MapPin, Phone, Receipt, Send, ShieldCheck, ShoppingBag, Wallet, X } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { getDistrictsByCity, getPostalCode, taiwanCities } from "@/data/taiwanAddress";
import AdminLayout from "./AdminLayout";
import {
  AdminMember,
  AdminMemberUpdatePayload,
  AdminUser,
  getAdminMember,
  getCurrentAdmin,
  logout,
  resendAdminMemberVerification,
  sendAdminMemberPasswordReset,
  setAdminMemberPassword,
  updateAdminMember,
  verifyAdminMember,
} from "./api";
import AdminPasswordInput from "./components/AdminPasswordInput";
import { useConfirmDialog } from "./components/ConfirmDialog";

const genderLabels: Record<string, string> = { Male: "男", Female: "女" };
const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

type EditFormState = {
  Name: string;
  Email: string;
  Phone: string;
  Gender: string;
  BirthDate: string;
  AddressCity: string;
  AddressDistrict: string;
  AddressLine: string;
};

function memberToEditForm(member: AdminMember): EditFormState {
  return {
    Name: member.Name,
    Email: member.Email,
    Phone: member.Phone || "",
    Gender: member.Gender || "",
    BirthDate: member.BirthDate || "",
    AddressCity: member.AddressCity || "",
    AddressDistrict: member.AddressDistrict || "",
    AddressLine: member.AddressLine || "",
  };
}

function getInitial(name: string) {
  const trimmed = name.trim();
  return trimmed ? trimmed.slice(0, 1).toUpperCase() : "?";
}

function InfoField({ icon, label, value }: { icon: ReactNode; label: string; value: ReactNode }) {
  return (
    <div className="rounded-md bg-[#f6f7f5] p-3">
      <div className="flex items-center gap-1.5 text-xs font-bold text-[#7b877f]">
        {icon}
        {label}
      </div>
      <p className="mt-1.5 break-words text-sm font-bold leading-relaxed text-[#203d32]">{value}</p>
    </div>
  );
}

export default function AdminMemberDetail() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [member, setMember] = useState<AdminMember | null>(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const [passwordFormOpen, setPasswordFormOpen] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");
  const [passwordMessage, setPasswordMessage] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState<EditFormState | null>(null);
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load() {
    if (!id) return;
    setLoading(true);
    setMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await getAdminMember(id);
      setMember(data.Member);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "會員資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  async function handleToggleActive() {
    if (!member) return;
    const nextActive = !member.IsActive;
    const confirmed = await confirm({
      title: nextActive ? "啟用此會員帳號？" : "停用此會員帳號？",
      description: nextActive
        ? "啟用後，此會員可正常登入並使用購物功能。"
        : "停用後，此會員將無法登入會員中心（既有訂單紀錄不受影響）。",
      confirmText: nextActive ? "啟用帳號" : "停用帳號",
      cancelText: "取消",
      tone: nextActive ? "info" : "danger",
    });
    if (!confirmed) return;

    setSaving(true);
    setMessage("");
    try {
      const data = await updateAdminMember(member.MemberId, { IsActive: nextActive });
      setMember(data.Member);
      setMessage(nextActive ? "會員帳號已啟用。" : "會員帳號已停用。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "操作失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  }

  function openEditForm() {
    if (!member) return;
    setEditForm(memberToEditForm(member));
    setEditMessage("");
    setEditOpen(true);
  }

  function closeEditForm() {
    setEditOpen(false);
    setEditMessage("");
  }

  function handleEditCityChange(city: string) {
    setEditForm((current) => (current ? { ...current, AddressCity: city, AddressDistrict: "" } : current));
  }

  async function handleEditSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!member || !editForm) return;

    setEditSaving(true);
    setEditMessage("");
    try {
      const payload: AdminMemberUpdatePayload = {
        Name: editForm.Name.trim(),
        Email: editForm.Email.trim(),
        Phone: editForm.Phone.trim(),
        Gender: editForm.Gender,
        BirthDate: editForm.BirthDate,
        AddressCity: editForm.AddressCity,
        AddressDistrict: editForm.AddressDistrict,
        AddressPostalCode: getPostalCode(editForm.AddressCity, editForm.AddressDistrict),
        AddressLine: editForm.AddressLine.trim(),
      };
      const data = await updateAdminMember(member.MemberId, payload);
      setMember(data.Member);
      setEditOpen(false);
      setMessage("會員基本資料已更新。");
    } catch (error) {
      setEditMessage(error instanceof Error ? error.message : "更新失敗，請稍後再試。");
    } finally {
      setEditSaving(false);
    }
  }

  async function handleManualVerify() {
    if (!member) return;
    const confirmed = await confirm({
      title: "手動標記為已驗證？",
      description: "請確認已透過其他方式（例如電話）確認此 Email 為會員本人所有，再手動標記為已驗證。",
      confirmText: "標記為已驗證",
      cancelText: "取消",
    });
    if (!confirmed) return;

    setSaving(true);
    setMessage("");
    try {
      const data = await verifyAdminMember(member.MemberId);
      setMember(data.Member);
      setMessage("已將此會員標記為 Email 已驗證。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "操作失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  }

  async function handleResendVerification() {
    if (!member) return;
    setSaving(true);
    setMessage("");
    try {
      await resendAdminMemberVerification(member.MemberId);
      setMessage("已重新寄出驗證信。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "寄送失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  }

  async function handleSendPasswordReset() {
    if (!member) return;
    const confirmed = await confirm({
      title: "寄送密碼重設信？",
      description: "系統將寄出一封密碼重設信到此會員的 Email，會員需自行點擊信中連結設定新密碼（連結 2 小時內有效）。",
      confirmText: "寄送重設信",
      cancelText: "取消",
    });
    if (!confirmed) return;

    setSaving(true);
    setMessage("");
    try {
      await sendAdminMemberPasswordReset(member.MemberId);
      setMessage("已寄出密碼重設信，請提醒會員至信箱查收。");
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "寄送失敗，請稍後再試。");
    } finally {
      setSaving(false);
    }
  }

  async function handleSetPassword(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!member) return;
    if (newPassword.length < 8) {
      setPasswordMessage("新密碼至少需要 8 碼。");
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setPasswordMessage("兩次輸入的新密碼不一致。");
      return;
    }

    const confirmed = await confirm({
      title: "直接設定新密碼？",
      description: "此操作會立即覆蓋會員目前的密碼，且會員先前收到的密碼重設信連結將同時失效，請確認是經過會員本人同意（例如電話協助）。",
      confirmText: "設定新密碼",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    setPasswordSaving(true);
    setPasswordMessage("");
    try {
      const data = await setAdminMemberPassword(member.MemberId, newPassword);
      setMember(data.Member);
      setNewPassword("");
      setConfirmNewPassword("");
      setPasswordFormOpen(false);
      setMessage("已為此會員設定新密碼。");
    } catch (error) {
      setPasswordMessage(error instanceof Error ? error.message : "設定失敗，請稍後再試。");
    } finally {
      setPasswordSaving(false);
    }
  }

  return (
    <AdminLayout sectionLabel="會員管理" title={member ? `會員 ${member.Name}` : "會員詳情"} user={currentUser} onLogout={handleLogout}>
      <ConfirmDialog />
      <button className="mb-4 inline-flex h-10 items-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] hover:bg-[#f0f5f2]" onClick={() => navigate("/admin/members")} type="button">
        <ArrowLeft size={18} />
        返回會員列表
      </button>

      {message && <div className="mb-4 rounded-md border border-[#dfe6e1] bg-white px-4 py-3 text-sm font-medium text-[#315746]">{message}</div>}

      {loading ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">載入中...</div>
      ) : !member ? (
        <div className="rounded-md border border-[#dfe6e1] bg-white p-8 text-center text-[#59665f]">查無此會員。</div>
      ) : (
        <div className="grid items-start gap-4 lg:grid-cols-3">
          <div className="grid gap-4 lg:col-span-2">
            <section className="rounded-md border border-[#dfe6e1] bg-white p-4 md:p-5">
              <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex min-w-0 items-center gap-3">
 
                  <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <h2 className="truncate text-lg font-bold text-[#203d32]">{member.Name}</h2>
                      {member.Gender && (
                        <span className="inline-flex shrink-0 items-center rounded-full bg-[#eef3f8] px-2.5 py-0.5 text-xs font-bold text-[#255071]">
                          {genderLabels[member.Gender] || member.Gender}
                        </span>
                      )}
                    </div>
                    <p className="mt-0.5 flex items-center gap-1.5 truncate text-sm text-[#7b877f]">
                      <Mail className="shrink-0" size={13} />
                      {member.Email}
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button
                    className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-[#cfd8d2] bg-white px-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                    onClick={editOpen ? closeEditForm : openEditForm}
                    type="button"
                  >
                    {editOpen ? <X size={15} /> : <Edit size={15} />}
                    {editOpen ? "取消編輯" : "編輯資料"}
                  </button>
                </div>
              </div>

              {editOpen && editForm ? (
                <form className="mt-5 grid gap-3 border-t border-[#edf0ee] pt-4" onSubmit={handleEditSubmit}>
                  {editMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-2.5 text-sm font-medium text-[#8a1f1f]">{editMessage}</div>}
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      姓名
                      <input className={inputClass} value={editForm.Name} onChange={(event) => setEditForm((current) => (current ? { ...current, Name: event.target.value } : current))} />
                    </label>
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      Email
                      <input className={inputClass} type="email" value={editForm.Email} onChange={(event) => setEditForm((current) => (current ? { ...current, Email: event.target.value } : current))} />
                    </label>
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      聯絡電話
                      <input className={inputClass} value={editForm.Phone} onChange={(event) => setEditForm((current) => (current ? { ...current, Phone: event.target.value } : current))} />
                    </label>
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      性別
                      <select className={`${inputClass} bg-white`} value={editForm.Gender} onChange={(event) => setEditForm((current) => (current ? { ...current, Gender: event.target.value } : current))}>
                        <option value="">未設定</option>
                        <option value="Male">男</option>
                        <option value="Female">女</option>
                      </select>
                    </label>
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      生日
                      <input className={inputClass} type="date" value={editForm.BirthDate} onChange={(event) => setEditForm((current) => (current ? { ...current, BirthDate: event.target.value } : current))} />
                    </label>
                  </div>

                  <div className="grid gap-3 border-t border-[#edf0ee] pt-3 sm:grid-cols-3">
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      縣市
                      <select className={`${inputClass} bg-white`} value={editForm.AddressCity} onChange={(event) => handleEditCityChange(event.target.value)}>
                        <option value="">請選擇縣市</option>
                        {taiwanCities.map((city) => <option key={city} value={city}>{city}</option>)}
                      </select>
                    </label>
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      鄉鎮市區
                      <select
                        className={`${inputClass} bg-white`}
                        disabled={!editForm.AddressCity}
                        value={editForm.AddressDistrict}
                        onChange={(event) => setEditForm((current) => (current ? { ...current, AddressDistrict: event.target.value } : current))}
                      >
                        <option value="">{editForm.AddressCity ? "請選擇鄉鎮市區" : "請先選擇縣市"}</option>
                        {getDistrictsByCity(editForm.AddressCity).map((district) => <option key={district.name} value={district.name}>{district.name}</option>)}
                      </select>
                    </label>
                    <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                      郵遞區號
                      <input className={`${inputClass} bg-[#f6f7f5] text-[#7b877f]`} readOnly value={getPostalCode(editForm.AddressCity, editForm.AddressDistrict)} />
                    </label>
                  </div>
                  <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                    詳細地址
                    <input className={inputClass} value={editForm.AddressLine} onChange={(event) => setEditForm((current) => (current ? { ...current, AddressLine: event.target.value } : current))} />
                  </label>

                  <div className="flex justify-end gap-2 pt-1">
                    <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeEditForm} type="button">
                      取消
                    </button>
                    <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60" disabled={editSaving} type="submit">
                      {editSaving ? "儲存中" : "儲存變更"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="mt-5 grid grid-cols-1 gap-3 border-t border-[#edf0ee] pt-4 sm:grid-cols-2 lg:grid-cols-3">
                  <InfoField icon={<Phone size={14} />} label="聯絡電話" value={member.Phone || "-"} />
                  <InfoField icon={<Calendar size={14} />} label="生日" value={member.BirthDate || "-"} />
                  <InfoField
                    icon={<MapPin size={14} />}
                    label="聯絡地址"
                    value={
                      member.AddressCity || member.AddressLine ? (
                        <>
                          {member.AddressPostalCode} {member.AddressCity}{member.AddressDistrict}
                          <br />
                          {member.AddressLine}
                        </>
                      ) : (
                        "-"
                      )
                    }
                  />
                </div>
              )}
            </section>

            <section className="grid gap-3 rounded-md border border-[#dfe6e1] bg-white p-4">
              <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <div className="flex items-center gap-3 rounded-md bg-[#f6f7f5] p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#e9f4ec] text-[#245438]">
                    <Receipt size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[#7b877f]">累積訂單數</p>
                    <p className="text-xl font-bold text-[#203d32]">{member.OrderCount ?? 0}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md bg-[#e9f4ec] p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-[#245438]">
                    <Wallet size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[#4c6b5b]">已付款金額</p>
                    <p className="text-xl font-bold text-[#203d32]">NT$ {(member.PaidAmount ?? 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md bg-[#f3ecdf] p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-[#8a6a2f]">
                    <Banknote size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[#8a6a2f]">待付款金額</p>
                    <p className="text-xl font-bold text-[#203d32]">NT$ {(member.UnpaidAmount ?? 0).toLocaleString()}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-md bg-[#fdebea] p-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-white text-[#8a1f1f]">
                    <CircleSlash size={18} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-[#8a1f1f]">已退款金額</p>
                    <p className="text-xl font-bold text-[#203d32]">NT$ {(member.RefundedAmount ?? 0).toLocaleString()}</p>
                  </div>
                </div>
              </div>
              <button
                className="flex items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white p-3 text-sm font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => navigate(`/admin/orders?Keyword=${encodeURIComponent(member.Email)}`)}
                type="button"
              >
                <ShoppingBag size={18} />
                查看訂單記錄
              </button>
            </section>
          </div>

          <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4">
            <h2 className="text-base font-bold text-[#315746]">帳號狀態</h2>

            <div className="flex items-center justify-between rounded-md bg-[#f6f7f5] px-3 py-3">
              <div>
                <p className="text-sm font-bold text-[#203d32]">帳號啟用狀態</p>
                <p className="mt-0.5 text-xs text-[#7b877f]">{member.IsActive ? "會員可正常登入" : "會員目前無法登入"}</p>
              </div>
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${member.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>
                {member.IsActive ? "啟用中" : "已停用"}
              </span>
            </div>
            <button
              className={`inline-flex h-11 items-center justify-center gap-2 rounded-md px-5 font-bold text-white transition disabled:opacity-60 ${
                member.IsActive ? "bg-[#9a2d2d] hover:bg-[#7c2424]" : "bg-[#315746] hover:bg-[#203d32]"
              }`}
              disabled={saving}
              onClick={handleToggleActive}
              type="button"
            >
              <ShieldCheck size={18} />
              {member.IsActive ? "停用此帳號" : "啟用此帳號"}
            </button>

            <div className="flex items-center justify-between rounded-md bg-[#f6f7f5] px-3 py-3">
              <div>
                <p className="text-sm font-bold text-[#203d32]">Email 驗證狀態</p>
                <p className="mt-0.5 text-xs text-[#7b877f]">{member.IsEmailVerified ? "已完成信箱驗證" : "尚未完成信箱驗證"}</p>
              </div>
              <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${member.IsEmailVerified ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#f3ecdf] text-[#8a6a2f]"}`}>
                {member.IsEmailVerified ? "已驗證" : "未驗證"}
              </span>
            </div>
            {!member.IsEmailVerified && (
              <div className="grid gap-2 sm:grid-cols-2">
                <button
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60"
                  disabled={saving}
                  onClick={handleResendVerification}
                  type="button"
                >
                  <Mail size={18} />
                  補寄驗證信
                </button>
                <button
                  className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-4 font-bold text-white transition hover:bg-[#203d32] disabled:opacity-60"
                  disabled={saving}
                  onClick={handleManualVerify}
                  type="button"
                >
                  <ShieldCheck size={18} />
                  手動標記已驗證
                </button>
              </div>
            )}

            <div className="border-t border-[#edf0ee] pt-3 text-xs text-[#7b877f]">
              <p>最後登入：{member.LastLoginAt || "尚未登入"}</p>
              <p className="mt-1">註冊時間：{member.CreatedAt || "-"}</p>
            </div>
          </section>

          <section className="grid gap-4 rounded-md border border-[#dfe6e1] bg-white p-4 lg:col-span-3">
            <h2 className="text-base font-bold text-[#315746]">密碼管理</h2>
            <p className="-mt-2 text-xs text-[#7b877f]">
              客服可協助會員重設密碼：建議優先使用「寄送密碼重設信」，讓會員自行設定；僅在會員無法收信、且已確認為本人的情況下，才使用「直接設定新密碼」。
            </p>

            <div className="grid gap-3 sm:grid-cols-2">
              <button
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2] disabled:opacity-60"
                disabled={saving}
                onClick={handleSendPasswordReset}
                type="button"
              >
                <Send size={18} />
                寄送密碼重設信
              </button>
              <button
                className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]"
                onClick={() => {
                  setPasswordFormOpen((current) => !current);
                  setPasswordMessage("");
                }}
                type="button"
              >
                <KeyRound size={18} />
                {passwordFormOpen ? "取消直接設定" : "直接設定新密碼"}
              </button>
            </div>

            {passwordFormOpen && (
              <form className="grid gap-3 rounded-md bg-[#f6f7f5] p-3 sm:grid-cols-2" onSubmit={handleSetPassword}>
                {passwordMessage && (
                  <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-2.5 text-sm font-medium text-[#8a1f1f] sm:col-span-2">{passwordMessage}</div>
                )}
                <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                  新密碼
                  <AdminPasswordInput
                    autoComplete="new-password"
                    className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    id="member-new-password"
                    value={newPassword}
                    onChange={setNewPassword}
                  />
                </label>
                <label className="grid gap-1.5 text-sm font-bold text-[#315746]">
                  確認新密碼
                  <AdminPasswordInput
                    autoComplete="new-password"
                    className="h-11 w-full rounded-md border border-[#cfd8d2] bg-white px-3 font-normal outline-none transition focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                    id="member-confirm-password"
                    value={confirmNewPassword}
                    onChange={setConfirmNewPassword}
                  />
                </label>
                <div className="sm:col-span-2">
                  <button
                    className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={passwordSaving}
                    type="submit"
                  >
                    <KeyRound size={18} />
                    {passwordSaving ? "設定中" : "確認設定新密碼"}
                  </button>
                </div>
              </form>
            )}
          </section>
        </div>
      )}
    </AdminLayout>
  );
}
